import { defineStore } from 'pinia';
import { useAuthStore } from '@/stores/auth'
import type { MelProduct, MelStoreState, ExcelAnalysisData, ExcelAnalysisRow, MelReference } from '@/types/mel';
import {ProduitCatalogue} from "@/types";


// Base keys (will be namespaced per logged-in vendeur)
const STORAGE_KEY_BASE = 'mel-products';
const STORAGE_DATE_KEY_BASE = 'mel-products-date';
const EXCEL_STORAGE_KEY_BASE = 'mel-excel-analysis';
const EXCEL_DATE_KEY_BASE = 'mel-excel-date';
const REFERENCES_KEY_BASE = 'mel-references';

// Helper to namespace localStorage keys per vendeur account
function getScopedKey(base: string): string {
  try {
    const auth = useAuthStore()
    const id = auth?.user?.id_vendeur || auth?.user?.id || 'guest'
    return `${base}:${id}`
  } catch {
    // Fallback in case store not available during SSR/init
    return `${base}:guest`
  }
}
const MAX_AGE_DAYS = 30;

export const useMelStore = defineStore('mel', {
  state: (): MelStoreState => ({
    productsInPreparation: [],
    lastSaved: null,
    excelAnalysis: null,
    references: [],
    prefillProduct: null,
    draftProducts: [], // 从数据库加载的草稿产品（premel='1' AND id_shooting IS NULL）
  }),

  getters: {
    /**
     * Nombre total de pièces en préparation
     */
    totalPieces: (state): number => {
      return state.productsInPreparation.length;
    },

    /**
     * Vérifie si des produits sont en attente
     */
    hasProducts: (state): boolean => {
      return state.productsInPreparation.length > 0;
    },

    /**
     * Vérifie si on peut ajouter plus de produits
     * (limite de 60 produits par demande)
     */
    canAddMore: (state): boolean => {
      return state.productsInPreparation.length < 60;
    },

    /**
     * Retourne le nombre de jours depuis la dernière sauvegarde
     */
    daysSinceLastSave: (state): number | null => {
      if (!state.lastSaved) return null;
      return Math.floor((Date.now() - state.lastSaved) / (1000 * 60 * 60 * 24));
    },
  },

  actions: {
    /**
     * Réinitialiser l'état en mémoire (sans toucher au localStorage)
     * Utile lors d'un changement de compte pour recharger les données
     */
    resetInMemory() {
      this.productsInPreparation = []
      this.references = []
      this.excelAnalysis = null
      this.lastSaved = null
    },

    /**
     * Ajouter un produit à la liste en préparation
     */
    addProduct(product: MelProduct) {
      // Générer un ID temporaire unique
      const tempId = `temp-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
      
      this.productsInPreparation.push({
        ...product,
        tempId,
      });
      
      this.saveToLocalStorage();
    },

    /**
     * Supprimer un produit de la liste
     */
    removeProduct(tempId: string) {
      const index = this.productsInPreparation.findIndex(p => p.tempId === tempId);
      if (index !== -1) {
        this.productsInPreparation.splice(index, 1);
        this.saveToLocalStorage();
      }
    },
      /**
       * Enregistrer un produit du catalogue pour pré-remplir le formulaire MEL
       */
      setPrefillProduct(product: ProduitCatalogue | null) {
          this.prefillProduct = product
      },

      /**
       * Nettoyer le produit pré-rempli une fois utilisé
       */
      clearPrefillProduct() {
          this.prefillProduct = null
      },

    /**
     * Modifier un produit existant
     */
    updateProduct(tempId: string, updatedProduct: Partial<MelProduct>) {
      const index = this.productsInPreparation.findIndex(p => p.tempId === tempId);
      if (index !== -1) {
        this.productsInPreparation[index] = {
          ...this.productsInPreparation[index],
          ...updatedProduct,
          tempId, // Garder le même tempId
        };
        this.saveToLocalStorage();
      }
    },

    /**
     * Vider tous les produits en préparation
     */
    clearAll() {
      this.productsInPreparation = [];
      this.lastSaved = null;
      localStorage.removeItem(getScopedKey(STORAGE_KEY_BASE));
      localStorage.removeItem(getScopedKey(STORAGE_DATE_KEY_BASE));
    },

    /**
     * Sauvegarder dans le LocalStorage
     */
    saveToLocalStorage() {
      try {
        const data = JSON.stringify(this.productsInPreparation);
        
        // Vérifier la taille (~5MB max)
        if (data.length > 5 * 1024 * 1024) {
          throw new Error('Trop de produits en attente. Veuillez valider votre demande.');
        }
        
        localStorage.setItem(getScopedKey(STORAGE_KEY_BASE), data);
        this.lastSaved = Date.now();
        localStorage.setItem(getScopedKey(STORAGE_DATE_KEY_BASE), this.lastSaved.toString());
      } catch (e) {
        
        if (e instanceof Error && e.name === 'QuotaExceededError') {
          throw new Error('Limite de stockage atteinte. Veuillez valider vos produits en attente.');
        }
        
        if (e instanceof Error) {
          throw e;
        }
        
        throw new Error('Erreur lors de la sauvegarde');
      }
    },

    /**
     * Charger depuis le LocalStorage
     */
    loadFromLocalStorage(): boolean {
      try {
        const saved = localStorage.getItem(getScopedKey(STORAGE_KEY_BASE));
        const savedDate = localStorage.getItem(getScopedKey(STORAGE_DATE_KEY_BASE));
        
        if (!saved) {
          return false;
        }

        // Vérifier l'âge des données
        if (savedDate) {
          const daysSince = (Date.now() - Number(savedDate)) / (1000 * 60 * 60 * 24);
          
          if (daysSince > MAX_AGE_DAYS) {
            this.clearAll();
            return false;
          }
        }

        // Charger les données
        const parsed = JSON.parse(saved);
        
        if (Array.isArray(parsed)) {
          this.productsInPreparation = parsed;
          this.lastSaved = savedDate ? Number(savedDate) : null;
          return true;
        }
        
        return false;
      } catch (e) {
        this.clearAll();
        return false;
      }
    },

    /**
     * Vérifier et nettoyer les données expirées
     */
    checkExpiration(): boolean {
      const daysSince = this.daysSinceLastSave;
      
      if (daysSince && daysSince > MAX_AGE_DAYS) {
        this.clearAll();
        return true; // Données expirées et nettoyées
      }
      
      return false; // Données valides
    },

    // ========== GESTION EXCEL ==========

    /**
     * Sauvegarder l'analyse Excel
     */
    setExcelAnalysis(results: ExcelAnalysisRow[], errors: string[], warnings: string[]) {
      this.excelAnalysis = {
        results,
        errors,
        warnings,
        selectedCaracteristiques: {},
        analyzedAt: Date.now(),
      };
      this.saveExcelToLocalStorage();
    },

    /**
     * Ajouter/modifier les caractéristiques pour une ligne
     */
    setCaracteristiquesForLine(ligne: number, caracteristiques: number[]) {
      if (this.excelAnalysis) {
        this.excelAnalysis.selectedCaracteristiques[ligne] = caracteristiques;
        this.saveExcelToLocalStorage();
      }
    },

    /**
     * Vider l'analyse Excel
     */
    clearExcelAnalysis() {
      this.excelAnalysis = null;
      localStorage.removeItem(getScopedKey(EXCEL_STORAGE_KEY_BASE));
      localStorage.removeItem(getScopedKey(EXCEL_DATE_KEY_BASE));
    },

    /**
     * Sauvegarder l'Excel dans LocalStorage
     */
    saveExcelToLocalStorage() {
      try {
        if (!this.excelAnalysis) return;
        
        const data = JSON.stringify(this.excelAnalysis);
        localStorage.setItem(getScopedKey(EXCEL_STORAGE_KEY_BASE), data);
        localStorage.setItem(getScopedKey(EXCEL_DATE_KEY_BASE), this.excelAnalysis.analyzedAt.toString());
      } catch (e) {
        // Erreur silencieuse lors de la sauvegarde
      }
    },

    /**
     * Charger l'Excel depuis LocalStorage
     */
    loadExcelFromLocalStorage(): boolean {
      try {
        const saved = localStorage.getItem(getScopedKey(EXCEL_STORAGE_KEY_BASE));
        const savedDate = localStorage.getItem(getScopedKey(EXCEL_DATE_KEY_BASE));
        
        if (!saved) {
          return false;
        }

        // Vérifier l'âge des données
        if (savedDate) {
          const daysSince = (Date.now() - Number(savedDate)) / (1000 * 60 * 60 * 24);
          
          if (daysSince > MAX_AGE_DAYS) {
            this.clearExcelAnalysis();
            return false;
          }
        }

        // Charger les données
        const parsed = JSON.parse(saved) as ExcelAnalysisData;
        this.excelAnalysis = parsed;
        return true;
      } catch (e) {
        this.clearExcelAnalysis();
        return false;
      }
    },

    // ========== GESTION RÉFÉRENCES ==========

    /**
     * Ajouter une référence au tableau
     */
    addReference(reference: MelReference) {
      this.references.push(reference);
      this.saveReferencesToLocalStorage();
    },

    /**
     * Supprimer une référence du tableau
     */
    removeReference(referenceId: string) {
      const index = this.references.findIndex(ref => ref.id === referenceId);
      if (index !== -1) {
        this.references.splice(index, 1);
        this.saveReferencesToLocalStorage();
      }
    },

    /**
     * Vider toutes les références
     */
    clearReferences() {
      this.references = [];
      localStorage.removeItem(getScopedKey(REFERENCES_KEY_BASE));
    },

    /**
     * Sauvegarder les références dans LocalStorage
     */
    saveReferencesToLocalStorage() {
      try {
        const data = JSON.stringify(this.references);
        localStorage.setItem(getScopedKey(REFERENCES_KEY_BASE), data);
      } catch (e) {
        // Erreur silencieuse lors de la sauvegarde
      }
    },

    /**
     * Charger les références depuis LocalStorage
     */
    loadReferencesFromLocalStorage(): boolean {
      try {
        const saved = localStorage.getItem(getScopedKey(REFERENCES_KEY_BASE));
        if (!saved) {
          return false;
        }

        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) {
          this.references = parsed;
          return true;
        }
        
        return false;
      } catch (e) {
        this.clearReferences();
        return false;
      }
    },

    // ========== GESTION DRAFT PRODUCTS (从数据库加载) ==========

    /**
     * 设置从数据库加载的草稿产品
     * 和旧版 PHP 一样：PRODUIT::GetAll with premel='1' AND id_shooting IS NULL
     */
    setDraftProducts(products: any[]) {
      this.draftProducts = products;
    },

    /**
     * 清空草稿产品
     */
    clearDraftProducts() {
      this.draftProducts = [];
    },

    /**
     * 删除单个草稿产品
     */
    removeDraftProduct(productId: string) {
      const index = this.draftProducts.findIndex((p: any) => p.id === productId || p.id_produit === productId);
      if (index !== -1) {
        this.draftProducts.splice(index, 1);
      }
    },

    /**
     * 获取草稿产品数量
     */
    getDraftProductsCount(): number {
      return this.draftProducts.length;
    },

  },
});

